import type { ComponentType } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Pencil } from 'lucide-react';
import { Button, Card } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { GoogleIcon, MicrosoftIcon } from '@/components/ProviderIcons.tsx';
import {
  INTEGRATION_PROVIDERS,
  fetchConnectedProviders,
  startCalendarLink,
  type IntegrationProvider,
} from '@/lib/integrations.ts';
import { m } from '@/paraglide/messages.js';

/*
 * The ask: which calendar Wundo may read.
 *
 * It renders whenever it is visited and never sends anybody on. That is the
 * difference between a step and a decision: a screen someone can be carried
 * past is a screen that never told them anything, which is exactly what
 * happened to the person coming back from a provider's consent screen. So
 * "already connected" is a state of this screen rather than a reason to skip
 * it — it says which calendar is linked, and offers the way forward.
 */
export const Route = createFileRoute('/onboarding/calendar')({
  /*
   * The one thing that does send somebody on: an organisation that has no
   * calendar integration at all. There is nothing to ask here then, and the
   * step after it — the conversation typed in by hand — is the whole of what
   * this branch has left. Everybody else renders the question.
   */
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData({
      queryKey: ['currentUser'],
      queryFn: fetchCurrentUser,
    });

    if (!user.calendar_enabled) {
      throw redirect({ to: '/onboarding/first-meeting', replace: true });
    }
  },
  component: CalendarRoute,
});

/* Reading the search is the route's job, so the screen itself stays renderable. */
function CalendarRoute() {
  const { error } = Route.useSearch();

  return <OnboardingCalendarPage linkFailed={error !== undefined} />;
}

const PROVIDER_NAMES: Record<IntegrationProvider, () => string> = {
  microsoft: m.onboarding_calendar_microsoft,
  google: m.onboarding_calendar_google,
};

/*
 * The suite is what people recognise; the calendar inside it is what they have
 * to match against their own. Naming both is what stops somebody with Outlook
 * from looking for a tile that says Outlook.
 */
const PROVIDER_SUBLABELS: Record<IntegrationProvider, () => string> = {
  microsoft: m.onboarding_calendar_microsoft_sub,
  google: m.onboarding_calendar_google_sub,
};

const PROVIDER_ICONS: Record<
  IntegrationProvider,
  ComponentType<{ className?: string }>
> = {
  microsoft: MicrosoftIcon,
  google: GoogleIcon,
};

export interface OnboardingCalendarPageProps {
  /** A provider bounced the user back here instead of granting access. */
  linkFailed: boolean;
}

export function OnboardingCalendarPage({ linkFailed }: OnboardingCalendarPageProps) {
  const navigate = useNavigate();

  const { data: connected, isPending } = useQuery({
    queryKey: ['integrations'],
    queryFn: fetchConnectedProviders,
  });
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * The browser goes to the provider on success, so this mutation has no
   * settled state worth rendering: it stays pending until the page is gone.
   *
   * Granting access answers this screen's question, so success returns to the
   * next step rather than to the question again. Coming back here still shows
   * the calendar as connected — that is for revisits, not for the grant itself.
   */
  const link = useMutation({
    mutationFn: (provider: IntegrationProvider) =>
      startCalendarLink(provider, `${window.location.origin}/onboarding/colleagues`, {
        meetRecorder: user?.meet_recorder_enabled === true,
      }),
  });

  /* Nothing rather than a guess: the wrong state here reads as lost work. */
  if (isPending) return null;

  /*
   * A check that could not be made is not a connected calendar. Offering the
   * ask is the only useful thing left, and connecting a calendar that is
   * already connected costs nothing but a second consent screen.
   */
  const linked = connected ?? { microsoft: false, google: false };
  const anyLinked = INTEGRATION_PROVIDERS.some((provider) => linked[provider]);
  const failed = linkFailed || link.isError;

  return (
    <Card padding='none' className='mx-auto w-full max-w-130 px-11 pt-11 pb-9'>
      <p className='mb-3.5 text-xs font-bold tracking-wider text-ink-muted uppercase'>
        {m.onboarding_calendar_kicker()}
      </p>
      <h1 className='mb-3 text-xl font-medium tracking-tight'>
        {m.onboarding_calendar_title()}
      </h1>
      <p className='max-w-120 text-md text-ink-muted'>
        {m.onboarding_calendar_subtitle()}
      </p>

      {failed && (
        <p
          role='alert'
          className='mt-6.5 rounded-lg border border-danger-line bg-danger-fill px-4 py-3 text-sm font-medium text-danger'
        >
          {m.onboarding_calendar_failed()}
        </p>
      )}

      <div className='mt-8 grid grid-cols-2 gap-3'>
        {INTEGRATION_PROVIDERS.map((provider) => {
          /*
           * A calendar that is already linked is reported, not offered again.
           * The other one stays connectable: people do keep two.
           */
          return linked[provider] ? (
            <ConnectedProvider key={provider} provider={provider} />
          ) : (
            <Button
              key={provider}
              variant='quiet'
              shape='block'
              disabled={link.isPending || user === undefined}
              onClick={() => link.mutate(provider)}
              className='h-auto flex-col gap-3 whitespace-normal rounded-xl border border-line bg-transparent px-4.5 pt-6.5 pb-5.5 hover:border-ink-faint hover:bg-fill-faint'
            >
              <ProviderTileBody provider={provider} />
            </Button>
          );
        })}

        {anyLinked && (
          <Button
            variant='accent'
            shape='block'
            size='lg'
            fullWidth
            onClick={() => void navigate({ to: '/onboarding/colleagues' })}
            className='col-span-2'
          >
            {m.onboarding_calendar_continue()}
          </Button>
        )}
      </div>

      {/*
        The way past, for somebody who has no calendar to give or whose
        administrator has to grant it first. It used to be a link so quiet that
        the step read as a dead end without a calendar, so it is a box of its
        own now — dashed rather than filled, because a calendar is still the
        better answer and this is the alternative, not the second offer.

        It leads straight to writing a conversation down: the page of facts that
        used to sit in between told people what they were missing before they
        had seen Wundo do anything at all.
      */}
      <Button
        variant='quiet'
        shape='block'
        fullWidth
        onClick={() => void navigate({ to: '/onboarding/first-meeting' })}
        className='mt-6 h-auto flex-col items-start gap-1 border border-dashed border-line bg-transparent px-5 py-4.5 text-left whitespace-normal hover:border-ink-faint hover:bg-fill-faint'
      >
        <span className='flex items-center gap-2'>
          <Pencil aria-hidden='true' className='size-4 shrink-0 text-ink-muted' />
          <span className='text-base font-bold text-ink underline underline-offset-2'>
            {m.onboarding_calendar_skip()}
          </span>
        </span>
        <span className='text-sm font-normal text-ink-muted'>
          {m.onboarding_calendar_skip_sub()}
        </span>
      </Button>

      {/* The way out, said where the ask is rather than somewhere in settings. */}
      <p className='mt-5.5 text-center text-sm text-ink-muted'>
        {m.onboarding_calendar_reassurance()}
      </p>
    </Card>
  );
}

/* Same shape as the tile it stands in for, so the pair stays even. */
function ConnectedProvider({ provider }: { provider: IntegrationProvider }) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 rounded-xl border border-line px-4.5 pt-6.5 pb-5.5 text-center'>
      <ProviderTileBody provider={provider} />
      <span className='flex items-center gap-1.5 text-sm font-bold text-success'>
        <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
        {m.onboarding_calendar_connected()}
      </span>
    </div>
  );
}

/*
 * One provider as the mockup draws it: the mark on a chip, the suite's name,
 * and under it what Wundo actually reaches once it is connected.
 */
function ProviderTileBody({ provider }: { provider: IntegrationProvider }) {
  const Icon = PROVIDER_ICONS[provider];

  return (
    <>
      <span className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-fill-faint'>
        <Icon className='size-6' />
      </span>
      <span className='flex flex-col items-center gap-0.5'>
        <span className='text-base font-bold text-ink'>{PROVIDER_NAMES[provider]()}</span>
        <span className='text-sm font-normal text-ink-muted'>
          {PROVIDER_SUBLABELS[provider]()}
        </span>
      </span>
    </>
  );
}
